<template>
  <ZubieDialog
    ref="dialog"
    content-class="row justify-center items-center"
    :content-style="`max-width: ${!$q.screen.gt.sm ? 'auto' : '500px'};`"
    :dirty="isDirty"
    :maximized="$q.screen.xs"
    :processing="isLoading"
    :title="vehicleKey ? 'Edit Vehicle' : 'Add Vehicle'"
    @hide="$emit('hide')"
  >
    <transition name="slide">
      <div v-if="!isLoading" class="q-pa-none q-pb-sm full-width">
        <div v-if="errorMessage" class="bg-negative text-white q-pa-md q-mb-lg">{{ errorMessage }}</div>
        <div class="q-gutter-md">
          <div class="form-fields-wrapper q-px-md">
            <q-input
              ref="nickname"
              v-model="vehicle.nickname"
              :error="Boolean(errors.nickname)"
              :error-message="errors.nickname ? errors.nickname[0] : validations[0]('')"
              filled
              label="Vehicle Nickname"
              lazy-rules
              :rules="validations"
              @input="
                isDirty = true;
                errors.nickname = false;
              "
            />
            <div class="row" :class="{ row: $q.screen.gt.xs, column: $q.screen.xs, 'q-mb-md': vehicleKey }">
              <div class="col">
                <q-input
                  ref="vin"
                  v-model="vehicle.vin"
                  :class="{
                    'has-hint': vehicle.key && !vehicle.isVinEditable,
                  }"
                  :disable="vehicle.key && !vehicle.isVinEditable"
                  :error="Boolean(errors.vin)"
                  :error-message="errors.vin ? errors.vin[0] : validations[0]('')"
                  filled
                  :hint="vehicle.key && !vehicle.isVinEditable ? 'Reported by vehicle and cannot be edited' : null"
                  label="VIN"
                  lazy-rules
                  :rules="validations"
                  @input="
                    isDirty = true;
                    errors.vin = false;
                  "
                />
                <VehicleStyleField
                  v-if="vehicleKey"
                  :vehicle="vehicle"
                  @input="
                    ({ style, hasInitialized }) => {
                      vehicle.styleId = style.vehicleId;
                      vehicle.styleProvider = 'dataone';

                      if (hasInitialized) {
                        isDirty = true;
                      }
                    }
                  "
                />
              </div>
              <AssetPicture
                v-if="vehicleKey"
                :class="{
                  'q-ml-md col-auto column justify-center': $q.screen.gt.xs,
                  'row justify-center q-mt-md': $q.screen.xs,
                }"
                :imageUrl="vehicle.imageUrl"
                :majorType="ASSET_TYPES.VEHICLE"
                :type="ASSET_TYPES.VEHICLE"
                @remove="
                  vehicle.imageUrl = '';
                  isDirty = true;
                "
                @update="
                  vehicle.imageUrl = $event;
                  $forceUpdate();
                  isDirty = true;
                "
              />
            </div>
            <q-input
              v-model="vehicle.odometer"
              class="has-hint"
              :disable="vehicle.odometerIsActual"
              filled
              :hint="
                vehicle.odometerIsActual
                  ? 'Reported by vehicle and cannot be edited'
                  : 'Calculated using recorded trip distance'
              "
              label="Odometer"
              @input="isDirty = true"
            />
            <q-select
              v-model="vehicle.primaryDriverKey"
              v-capacitor-native-select
              class="q-mb-md"
              display-value-sanitize
              emit-value
              filled
              for="editVehiclePrimaryDriver"
              :hint="driverHint"
              label="Primary Driver"
              map-options
              :options="driversSelectOptions"
              options-sanitize
              @input="isDirty = true"
            />
            <ZubieSelect
              v-if="hasPermission('tagListView')"
              class="q-mb-md"
              display-value-sanitize
              emit-value
              filled
              for="editVehicleTags"
              label="Tags"
              map-options
              multiple
              option-label="value"
              option-value="key"
              :options="vehicleTagsTaggable"
              options-sanitize
              popup-label="Selected Tags"
              use-chips
              :value.sync="vehicle.tagKeys"
              @input="isDirty = true"
            >
              <template v-slot:selected-item="scope">
                <TagChip
                  dense
                  :removable="true"
                  :tabindex="scope.tabindex"
                  :tag="scope.opt"
                  @remove="vehicle.tagKeys.splice(scope.index, 1)"
                />
              </template>
            </ZubieSelect>
            <TreeViewMultiSelect
              v-if="hasPermission('groupListView')"
              :dataTree="groupsWithAccount"
              filled
              forId="editVehicleGroup"
              hint="Restricts visibility of this vehicle by assigning a group below the top-level account."
              label="Group"
              nodeLabelKey="name"
              nodeValueKey="key"
              :singleSelection="true"
              :value.sync="vehicle.groupKeys"
              @change="
                (value) => {
                  vehicle.groupKeys = value;
                  isDirty = true;
                }
              "
            />
            <PermittedDrivingHours
              v-if="hasPermission('vehicleScheduleCreate')"
              class="q-mb-md"
              forId="editVehicleDrivingHours"
              :value="vehicle.scheduleKey"
              @input="
                vehicle.scheduleKey = $event;
                isDirty = true;
              "
            />

            <FuelEconomyField
              :disabled="!hasPermission('vehicleEdit')"
              :economy="parseInt(vehicle.fuelEconomy, 10)"
              :economyUm="vehicle.fuelEconomyUm"
              :type="vehicle.fuelType"
              @input="
                onFuelFieldInput($event);
                isDirty = true;
              "
            />
          </div>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="isLoading" class="row justify-center">
        <q-spinner color="primary" size="3em" />
      </div>
    </transition>

    <template slot="actions">
      <q-btn
        v-close-popup
        class="q-px-sm"
        color="dark-grey"
        data-t="editVehicleCancel"
        :disable="savingVehicle"
        flat
        label="Cancel"
        rounded
        @click="handleCancel"
      />
      <q-btn
        class="q-px-sm"
        color="primary"
        data-t="editVehicleSave"
        :disable="savingVehicle || (vehicleKey && !vehicle.nickname)"
        rounded
        @click.prevent="handleSave"
      >
        <q-spinner v-if="savingVehicle" class="q-mr-sm"></q-spinner>
        {{ savingVehicle ? 'Saving' : 'Save' }}
      </q-btn>
    </template>
  </ZubieDialog>
</template>
<script>
import _cloneDeep from 'lodash/cloneDeep';
import ZubieSelect from 'components/ZubieSelect';
import AssetPicture from 'src/components/assets/AssetPicture';
import FuelEconomyField from 'components/vehicles-page/FuelEconomyField';
import PermittedDrivingHours from 'components/vehicles-page/PermittedDrivingHours';
import TagChip from 'src/components/tags/TagChip';
import TreeViewMultiSelect from 'src/components/TreeViewMultiSelect';
import VehicleStyleField from 'src/components/vehicles-page/VehicleStyleField';
import { mapGetters, mapActions, mapState } from 'vuex';
import _pick from 'lodash/pick';
import { ASSET_TYPES, ASSET_TYPE_NAMES } from 'src/services/constants';
import { hasValueFactory } from 'src/services/forms';
import { hasDriverCheckin } from 'src/services/vehicles';
import Vehicle from 'src/models/Vehicle';

const DEFAULT_VEHICLE_ODOMETER_UM = 'mi';

export default {
  name: 'EditVehicleDialog',
  props: ['vehicleKey'],
  components: {
    AssetPicture,
    FuelEconomyField,
    PermittedDrivingHours,
    TagChip,
    TreeViewMultiSelect,
    VehicleStyleField,
    ZubieSelect,
  },
  computed: {
    ...mapGetters('groups', ['groupsWithAccount']),
    ...mapGetters('session', ['hasPermission']),
    ...mapGetters('tags', ['vehicleTagsTaggable']),
    ...mapGetters('users', ['usersAsArray']),
    ...mapState('vehicles', ['detailedVehicles']),
    ASSET_TYPES() {
      return ASSET_TYPES;
    },
    ASSET_TYPE_NAMES() {
      return ASSET_TYPE_NAMES;
    },
    driverHint() {
      if (this.hasDriverCheckin) {
        const { checkInExpiration, fullName } = this.vehicle.driver;
        return `${fullName} is checked in to this vehicle until ${this.$options.filters.utcToLocalTimestamp(
          checkInExpiration
        )}`;
      }
      // the different return types are annoying here, but QSelect renders an empty hint if given anything (incl. null) but undefined
      return undefined;
    },
    driversSelectOptions() {
      const options = this.usersAsArray.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.key,
      }));
      options.sort((a, b) => (a.label > b.label ? 1 : -1));
      options.unshift({
        label: 'Unassigned',
        value: '',
      });
      return options;
    },
    hasDriverCheckin() {
      return hasDriverCheckin(this.vehicle.driver);
    },
    originalVehicle() {
      if (this.vehicleKey) {
        return this.detailedVehicles[this.vehicleKey];
      }
      return null;
    },
    validations() {
      return [hasValueFactory()];
    },
  },
  data() {
    return {
      errors: {},
      errorMessage: '',
      isLoading: false,
      isDirty: false,
      savingVehicle: false,
      newImage: null,
      vehicle: new Vehicle(),
    };
  },
  methods: {
    ...mapActions('groups', ['getGroups']),
    ...mapActions('users', ['getUsers']),
    ...mapActions('tags', ['getTags']),
    ...mapActions('vehicles', ['getDetailedVehicle', 'saveVehicle']),
    ...mapActions('vehicleStyle', ['clearStyle']),
    async handleSave() {
      this.errors = {};
      this.errorMessage = '';
      this.savingVehicle = true;

      const requiredFields = [this.$refs.nickname, this.$refs.vin];
      const validationResults = requiredFields.map((field) => field.validate());
      if (validationResults.some((result) => !result)) {
        this.savingVehicle = false;
        return;
      }

      // schedule, fuel not implemented :(
      const payload = _pick(
        this.vehicle,
        'fuelEconomy',
        'fuelEconomyUm',
        'fuelType',
        'groupKeys',
        'imageUrl',
        'nickname',
        'tagKeys',
        'vin'
      );

      // create vs. edit vehicle end points take different property names
      // new vehicle
      if (!this.vehicleKey) {
        payload.odometer = this.vehicle.odometer;
        payload.odometerUm = this.vehicle.odometerUm;

        // Set the primary driver if one is selected
        if (this.vehicle.primaryDriverKey) {
          payload.primaryDriverKey = this.vehicle.primaryDriverKey;
        }

        // create vehicle API doesn't accept empty schedule keys, so only include if set
        if (this.vehicle.scheduleKey) {
          payload.scheduleKey = this.vehicle.scheduleKey;
        }
      } else {
        payload.key = this.vehicleKey;
        payload.vehiclePrimaryUserKey = this.vehicle.primaryDriverKey;
        // only post odometer info if it has changed b/c saving it turns off true odometer until the next trip
        if (
          this.originalVehicle.odometer !== this.vehicle.odometer ||
          this.originalVehicle.odometerUm !== this.vehicle.odometerUm
        ) {
          payload.vehicleOdometer = this.vehicle.odometer;
          payload.vehicleOdometerUm = this.vehicle.odometerUm;
        }

        // only post zubieStyleId if someone has edited the vehicle's style
        if (this.vehicle.styleId !== this.originalVehicle.styleId) {
          payload.styleId = this.vehicle.styleId;
          payload.styleProvider = this.vehicle.styleProvider;
        }

        // Save fuel economy settings
        payload.fuelEconomy = this.vehicle.fuelEconomy;
        payload.fuelType = this.vehicle.fuelType;
        if (this.vehicle.fuelEconomyUm) {
          payload.fuelEconomyUm = this.vehicle.fuelEconomyUm;
        }

        // Save schedule
        payload.scheduleKey = this.vehicle.scheduleKey;
      }

      const response = await this.saveVehicle(payload);
      this.savingVehicle = false;
      if (response.error) {
        this.handleError(response);
      } else {
        this.$emit('ok');
        this.hide();
      }
    },
    handleError(response) {
      this.errors = response.error;
      if (typeof response.error !== 'object') {
        this.errorMessage = this.error;
      }
    },
    show() {
      this.$refs.dialog.show();
    },
    hide() {
      this.$refs.dialog.hide();
    },
    handleHide() {
      this.errors = {};
      this.errorMessage = '';
      this.clearStyle();
      this.$emit('hide');
    },
    handleCancel() {
      this.hide();
    },
    onFuelFieldInput({ economy, economyUm, type }) {
      this.vehicle.fuelEconomy = economy;
      this.vehicle.fuelEconomyUm = economyUm;
      this.vehicle.fuelType = type;
    },
  },
  async mounted() {
    this.isLoading = true;

    if (!this.usersAsArray.length) {
      this.getUsers();
    }
    if (!this.vehicleTagsTaggable.length) {
      this.getTags();
    }

    if (this.groupsWithAccount.length === 0) {
      await this.getGroups();
    }

    if (this.vehicleKey) {
      if (!this.detailedVehicles[this.vehicleKey]) {
        await this.getDetailedVehicle(this.vehicleKey);
      }
      const clone = _cloneDeep(this.detailedVehicles[this.vehicleKey]);
      // must define schedule key so it can be watched by PermittedDrivingHours
      if (!clone || !clone.scheduleKey) {
        clone.scheduleKey = '';
      }
      this.vehicle = clone;
    } else {
      this.vehicle = {
        odometerUm: DEFAULT_VEHICLE_ODOMETER_UM,
        primaryDriverKey: '',
        scheduleKey: '',
      };
    }

    this.isDirty = false;

    this.isLoading = false;
  },
};
</script>

<style lang="stylus" scoped>
.buttons-container {
  border-top: 1px solid #979797;
}

/deep/ .q-field--with-bottom {
  padding-bottom: 28px;
}

/deep/ .q-field__bottom {
  min-height: 0;
}

.fade-leave-active {
  transition: opacity 0.5s;
  opacity: 0;
}

.fade-leave, .fade-enter-to {
  opacity: 1;
}

.slide-enter-active, .slide-leave-active {
  transition: max-height 0.5s;
  max-height: calc(100vh - 100px);
}

.slide-enter, .slide-leave-to {
  max-height: 0;
}
</style>
